export type PciFreshnessState = "fresh" | "stale" | "expired";

export interface PciDataFreshness {
    status: "fresh" | "stale";
    fetchedAt: number;
    freshUntil: number;
    expiresAt: number;
}

export interface PciResponseHeaders {
    getResponseHeader(name: string): string | null;
}

export class PciDataStatus {
    private static activeTrackers = new WeakMap<HTMLElement, () => void>();

    public static FromResponse(response: PciResponseHeaders): PciDataFreshness | null {
        const status = response.getResponseHeader("X-PCI-Data-Status");
        const fetchedAt = Date.parse(response.getResponseHeader("X-PCI-Data-Fetched-At") || "");
        const freshUntil = Date.parse(response.getResponseHeader("X-PCI-Data-Fresh-Until") || "");
        const expiresAt = Date.parse(response.getResponseHeader("X-PCI-Data-Expires-At") || "");

        if ((status !== "fresh" && status !== "stale") ||
            !Number.isFinite(fetchedAt) || !Number.isFinite(freshUntil) || !Number.isFinite(expiresAt)) {
            return null;
        }

        return { status, fetchedAt, freshUntil, expiresAt };
    }

    public static Combine(
        first: PciDataFreshness | null,
        second: PciDataFreshness | null): PciDataFreshness | null {
        if (!first || !second) return first || second;

        return {
            status: first.status === "stale" || second.status === "stale" ? "stale" : "fresh",
            fetchedAt: Math.min(first.fetchedAt, second.fetchedAt),
            freshUntil: Math.min(first.freshUntil, second.freshUntil),
            expiresAt: Math.min(first.expiresAt, second.expiresAt)
        };
    }

    public static Watch(
        freshness: PciDataFreshness,
        onChange: (state: PciFreshnessState) => void): () => void {
        let timeoutId: number | null = null;
        let lastState: PciFreshnessState | null = null;
        let disposed = false;

        const update = () => {
            if (disposed || document.visibilityState === "hidden") return;

            const now = Date.now();
            const state: PciFreshnessState = now >= freshness.expiresAt
                ? "expired"
                : freshness.status === "stale" || now >= freshness.freshUntil
                    ? "stale"
                    : "fresh";

            if (state !== lastState) {
                lastState = state;
                onChange(state);
            }

            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
                timeoutId = null;
            }

            const nextBoundary = state === "fresh"
                ? freshness.freshUntil
                : state === "stale"
                    ? freshness.expiresAt
                    : null;

            if (nextBoundary !== null) {
                timeoutId = window.setTimeout(update, Math.max(0, nextBoundary - now));
            }
        };

        const onVisibilityChange = () => update();
        document.addEventListener("visibilitychange", onVisibilityChange);
        update();

        return () => {
            disposed = true;
            document.removeEventListener("visibilitychange", onVisibilityChange);
            if (timeoutId !== null) window.clearTimeout(timeoutId);
        };
    }

    public static Apply(
        statusElement: HTMLElement,
        dataElement: HTMLElement,
        response: PciResponseHeaders,
        onExpire: () => void): boolean {
        this.StopTracking(statusElement);
        const freshness = this.FromResponse(response);
        if (!freshness) {
            dataElement.hidden = true;
            dataElement.setAttribute("aria-hidden", "true");
            window.setTimeout(onExpire, 0);
            this.Render(
                statusElement,
                "error",
                "PCI data is unavailable",
                "The response did not include freshness information, so it cannot be shown safely. Refresh the page to try again.");
            return false;
        }

        const stop = this.Watch(freshness, state => {
            if (state === "expired") {
                dataElement.hidden = true;
                dataElement.setAttribute("aria-hidden", "true");
                window.setTimeout(onExpire, 0);
                this.Render(
                    statusElement,
                    "warning",
                    "PCI data is out of date",
                    "This data has reached its 15-minute limit and is no longer available. Refresh the page to check for newer data.");
                return;
            }

            dataElement.hidden = false;
            dataElement.removeAttribute("aria-hidden");
            if (state === "stale") {
                this.Render(
                    statusElement,
                    "warning",
                    "Showing last-known PCI data",
                    `This data was retrieved ${this.FormatDate(freshness.fetchedAt)}. Refresh the page to check for updates; it is available until ${this.FormatDate(freshness.expiresAt)}.`);
            } else {
                this.Render(statusElement, null, "", "");
            }
        });

        this.activeTrackers.set(statusElement, stop);
        return true;
    }

    public static ShowError(statusElement: HTMLElement, message: string): void {
        this.StopTracking(statusElement);
        this.Render(statusElement, "error", "Data is temporarily unavailable", message);
    }

    public static FormatDate(timestamp: number): string {
        const date = new Date(timestamp);
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const formatted = date.toLocaleString();
        return timeZone ? `${formatted} (${timeZone})` : formatted;
    }

    private static StopTracking(element: HTMLElement): void {
        const stop = this.activeTrackers.get(element);
        if (stop) {
            stop();
            this.activeTrackers.delete(element);
        }
    }

    private static Render(
        element: HTMLElement,
        tone: "warning" | "error" | null,
        heading: string,
        message: string): void {
        element.replaceChildren();
        if (!tone) return;

        const alert = document.createElement("div");
        alert.className = `pci-data-alert pci-data-alert--${tone}`;
        alert.setAttribute("role", tone === "error" ? "alert" : "status");

        const accent = document.createElement("span");
        accent.className = "pci-data-alert__accent";
        accent.setAttribute("aria-hidden", "true");

        const content = document.createElement("div");
        content.className = "pci-data-alert__content";

        const text = document.createElement("div");
        text.className = "pci-data-alert__text";

        const title = document.createElement("p");
        title.className = "pci-data-alert__heading";
        title.textContent = heading;

        const description = document.createElement("p");
        description.className = "pci-data-alert__message";
        description.textContent = message;

        text.append(title, description);
        content.append(text);
        alert.append(accent, content);
        element.append(alert);
    }
}
